import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Sun, Zap, Battery, Home, Car, Shield, Check, Phone, Package, TrendingUp, Flame } from "lucide-react";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import BannerSlider from "../components/BannerSlider";
import { getProducts, getBlogPosts, getBrandsConfig, getBestsellers } from "../lib/api";
import api from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import RecentlyViewed from "../components/RecentlyViewed";
import { PageLoader } from "../components/SunSpinner";
import { useCategories } from "../lib/useCache";

const CAT_ICONS = {
    "fve-panely": { Icon: Sun, color: "#ff9800", label: "FVE Panely" },
    "stridace": { Icon: Zap, color: "#4caf50", label: "Střídače" },
    "baterie": { Icon: Battery, color: "#2196f3", label: "Baterie" },
    "loxone": { Icon: Home, color: "#4caf50", label: "Loxone" },
    "nabijeci-stanice": { Icon: Car, color: "#9c27b0", label: "Nabíjení EV" },
    "kabely": { Icon: Shield, color: "#607d8b", label: "Kabely" },
};

const WHY_US = [
    { icon: Shield, title: "Certifikovaní instalatéři", desc: "Všichni technici mají certifikaci TAČR a bohaté zkušenosti s FVE systémy" },
    { icon: Check, title: "Záruka 25 let na panely", desc: "Poskytujeme nadstandardní záruku na výkon solárních panelů" },
    { icon: Zap, title: "Komplexní řešení na klíč", desc: "FVE, baterie, wallbox, Loxone - vše pod jednou střechou" },
    { icon: Sun, title: "Servis po celé ČR", desc: "Rychlý zásah a pravidelné revize po celé České republice" },
];

const BRANDS = ["GoodWe", "SolaX", "Dyness", "LONGi", "Aiko", "Loxone", "Helukabel", "Fronius", "ABB", "TIGO", "Sunways", "Wallbox"];

export default function HomePage() {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const { data: allCats = [] } = useCategories();
    const categories = useMemo(() => allCats.filter(c => !c.parent_id && c.is_active !== false).slice(0, 6), [allCats]);
    const [posts, setPosts] = useState([]);
    const [brandConfigs, setBrandConfigs] = useState([]);
    const [bestsellers, setBestsellers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [schemaOrg, setSchemaOrg] = useState(null);

    useEffect(() => {
        Promise.all([
            getProducts({ is_active: true, limit: 8, sort: "created_at_desc" }),
            getBlogPosts({ published_only: true, limit: 3 }),
            getBrandsConfig().catch(() => ({ data: [] })),
            getBestsellers({ limit: 8 }).catch(() => ({ data: { items: [] } })),
            api.get("/seo/schema-config/public").catch(() => ({ data: null })),
        ]).then(([pRes, bRes, brRes, bsRes, schemaRes]) => {
            setProducts(pRes.data?.items || []);
            setPosts(bRes.data?.items || []);
            setBrandConfigs(brRes.data || []);
            setBestsellers(bsRes.data?.items || []);
            setSchemaOrg(schemaRes.data);
        }).catch(() => {}).finally(() => setLoading(false));
    }, []);

    const orgLd = useMemo(() => {
        const s = schemaOrg || {};
        if (s.organization_schema_enabled === false) return null;
        const org = {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": s.org_name || "Solar-Green.cz",
            "url": s.org_url || "https://solar-green.cz",
            ...(s.org_legal_name ? { "legalName": s.org_legal_name } : {}),
            ...(s.org_description ? { "description": s.org_description } : {}),
            ...(s.org_logo ? { "logo": s.org_logo } : {}),
            ...(s.org_email ? { "email": s.org_email } : {}),
            ...(s.org_phone ? { "telephone": s.org_phone } : {}),
            ...(s.org_ico ? { "taxID": s.org_ico } : {}),
            ...(s.org_dic ? { "vatID": s.org_dic } : {}),
        };
        const social = [s.org_social_facebook, s.org_social_instagram, s.org_social_youtube].filter(Boolean);
        if (social.length) org.sameAs = social;
        if (s.org_address_street || s.org_address_city) {
            org.address = {
                "@type": "PostalAddress",
                ...(s.org_address_street ? { "streetAddress": s.org_address_street } : {}),
                ...(s.org_address_city ? { "addressLocality": s.org_address_city } : {}),
                ...(s.org_address_zip ? { "postalCode": s.org_address_zip } : {}),
                ...(s.org_address_country ? { "addressCountry": s.org_address_country } : {}),
            };
        }
        return JSON.stringify(org);
    }, [schemaOrg]);

    const websiteLd = useMemo(() => {
        const s = schemaOrg || {};
        if (s.search_action_enabled === false) return null;
        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": s.org_name || "Solar-Green.cz",
            "url": s.org_url || "https://solar-green.cz",
            "potentialAction": {
                "@type": "SearchAction",
                "target": (s.org_url || "https://solar-green.cz") + "/hledat?q={search_term_string}",
                "query-input": "required name=search_term_string"
            }
        });
    }, [schemaOrg]);

    const sv = theme.sections_visible || {};
    const defaultOrder = ["hero", "banners", "categories", "bestsellers", "featured_products", "why_us", "brands", "blog", "cta"];
    let orderedKeys = theme.sections_order || defaultOrder;
    // Ensure 'bestsellers' is included even if theme has an old sections_order
    if (!orderedKeys.includes("bestsellers")) {
        const catIdx = orderedKeys.indexOf("categories");
        orderedKeys = [...orderedKeys.slice(0, catIdx + 1), "bestsellers", ...orderedKeys.slice(catIdx + 1)];
    }

    const sectionMap = {
        hero: sv.hero !== false && <HeroSection theme={theme} />,
        banners: sv.banners !== false && <BannersSection />,
        categories: sv.categories !== false && categories.length > 0 && <CategoriesSection categories={categories} />,
        bestsellers: sv.bestsellers !== false && bestsellers.length > 0 && <BestsellersSection products={bestsellers} />,
        featured_products: sv.featured_products !== false && <FeaturedSection products={products} loading={loading} />,
        why_us: sv.why_us !== false && <WhyUsSection />,
        brands: sv.brands !== false && <BrandsSection brandConfigs={brandConfigs} />,
        blog: sv.blog !== false && posts.length > 0 && <BlogSection posts={posts} />,
        cta: sv.cta !== false && <CTASection theme={theme} />,
    };

    return (
        <div className="min-h-screen bg-gray-50/50">
            <Helmet>
                <title>Solar-Green.cz | Fotovoltaika, tepelná čerpadla, chytrá domácnost</title>
                <meta name="description" content={schemaOrg?.org_description || "Solar-Green.cz - Kompletní řešení pro fotovoltaiku, bateriové systémy, tepelná čerpadla a chytrou domácnost."} />
                <meta property="og:title" content="Solar-Green.cz | Fotovoltaika a chytrá domácnost" />
                <meta property="og:description" content={schemaOrg?.org_description || "Kompletní řešení pro fotovoltaiku, bateriové systémy, tepelná čerpadla a chytrou domácnost."} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={schemaOrg?.org_url || "https://solar-green.cz"} />
                {schemaOrg?.default_og_image && <meta property="og:image" content={schemaOrg.default_og_image} />}
                {orgLd && <script type="application/ld+json">{orgLd}</script>}
                {websiteLd && <script type="application/ld+json">{websiteLd}</script>}
            </Helmet>
            <Navbar />
            <PageLoader loading={loading}>
                <main>
                    {orderedKeys.map(key => sectionMap[key] ? <React.Fragment key={key}>{sectionMap[key]}</React.Fragment> : null)}
                    <div className="sg-container pb-8">
                        <RecentlyViewed maxShow={6} />
                    </div>
                </main>
            </PageLoader>
            <Footer />
        </div>
    );
}

function HeroSection({ theme }) {
    const navigate = useNavigate();
    const [heroProduct, setHeroProduct] = useState(null);

    useEffect(() => {
        api.get("/settings/hero-product").then(r => {
            if (r.data && r.data.name) setHeroProduct(r.data);
        }).catch(() => {});
    }, []);

    return (
        <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1a472a 0%, #2d7a3e 50%, #389622 100%)" }}>
            <div className="absolute inset-0">
                <div className="absolute top-10 left-10 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl" />
            </div>
            <div className="sg-container py-16 sm:py-20 relative z-10">
                <div className="flex items-center gap-8">
                    <div className="flex-1 max-w-2xl">
                        <p className="text-green-300 text-sm font-semibold mb-3 tracking-wide uppercase">
                            Solar-Green.cz - Váš partner pro zelenou energii
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-4">
                            {theme.hero_title || "FOTOVOLTAIKA"}
                        </h1>
                        <h2 className="text-xl sm:text-2xl font-bold text-white/90 mb-6">
                            {theme.hero_subtitle || "PRO VAŠI DOMÁCNOST"}
                        </h2>
                        <div className="flex items-center gap-6 mb-8 text-white/70 text-sm">
                            {theme.hero_stat1 && <span className="font-semibold text-white">{theme.hero_stat1}</span>}
                            {theme.hero_stat2 && <><span className="w-1 h-1 bg-white/30 rounded-full" /><span>{theme.hero_stat2}</span></>}
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button onClick={() => navigate("/kategorie")}
                                className="bg-white text-green-800 font-bold rounded-full px-8 h-12 text-base hover:bg-green-50 shadow-lg"
                                data-testid="hero-cta-btn">
                                Prozkoumat nabídku <ArrowRight size={16} className="ml-1" />
                            </Button>
                            <Button variant="outline" onClick={() => navigate("/fve")}
                                className="border-2 border-white/40 text-white font-bold rounded-full px-8 h-12 text-base hover:bg-white/10 hover:border-white/60">
                                O FVE více
                            </Button>
                        </div>
                    </div>

                    {/* Featured Product in Hexagon */}
                    {heroProduct && (
                        <div className="hidden lg:flex flex-col items-center relative" data-testid="hero-featured-product">
                            <div className="relative w-[340px] h-[340px] flex items-center justify-center">
                                <svg className="absolute inset-0 w-full h-full drop-shadow-2xl" viewBox="0 0 300 300">
                                    <polygon points="150,8 285,80 285,220 150,292 15,220 15,80"
                                        fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.15)" strokeWidth="2" />
                                </svg>
                                <Link to={`/produkt/${heroProduct.slug}`} className="relative z-10 group">
                                    {heroProduct.main_image ? (
                                        <img src={heroProduct.main_image} alt={heroProduct.name}
                                            className="w-72 h-72 object-contain drop-shadow-2xl group-hover:scale-105 transition-transform duration-300"
                                            style={{
                                                mixBlendMode: "multiply",
                                                maskImage: "radial-gradient(ellipse 75% 75% at center, black 45%, transparent 75%)",
                                                WebkitMaskImage: "radial-gradient(ellipse 75% 75% at center, black 45%, transparent 75%)",
                                            }}
                                            loading="lazy" />
                                    ) : (
                                        <Package size={80} className="text-white/30" />
                                    )}
                                </Link>
                            </div>
                            <Link to={`/produkt/${heroProduct.slug}`} className="text-center mt-1 group" data-testid="hero-product-link">
                                <p className="text-white/80 text-xs font-medium uppercase tracking-wider mb-1">Nejprodávanější</p>
                                <p className="text-white font-bold text-sm leading-tight group-hover:text-yellow-300 transition-colors max-w-[240px] line-clamp-2">{heroProduct.name}</p>
                                <p className="text-yellow-300 font-black text-xl mt-1">
                                    {(heroProduct.sale_price || heroProduct.price || 0).toLocaleString("cs-CZ")} Kč
                                </p>
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function BannersSection() {
    return (
        <div className="sg-container -mt-6 relative z-20 mb-8">
            <BannerSlider />
        </div>
    );
}

function CategoriesSection({ categories }) {
    return (
        <section className="sg-container py-10">
            <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-gray-900">Kategorie produktů</h2>
                <Link to="/kategorie" className="text-sm font-semibold text-green-600 hover:text-green-700 flex items-center gap-1">
                    Vše <ArrowRight size={14} />
                </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
                {categories.map((cat, i) => {
                    const fallback = CAT_ICONS[cat.slug] || { Icon: Package, color: "#389622" };
                    const hasImage = !!cat.image_url;
                    return (
                        <Link key={i} to={`/kategorie/${cat.slug}`}
                            className="group bg-white rounded-2xl p-6 flex flex-col items-center gap-4 border border-gray-100 hover:border-green-200 hover:shadow-lg hover:shadow-green-100/50 transition-all duration-300 hover:-translate-y-1 text-center"
                            data-testid={`home-cat-${cat.slug}`}>
                            <div className="w-24 h-24 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 overflow-hidden"
                                style={{ backgroundColor: hasImage ? "transparent" : (fallback.color + "15") }}>
                                {hasImage ? (
                                    <img src={cat.image_url} alt={cat.name} className="w-full h-full object-contain p-1" loading="lazy" />
                                ) : (
                                    <fallback.Icon size={40} style={{ color: fallback.color }} />
                                )}
                            </div>
                            <span className="text-base font-semibold text-gray-700 group-hover:text-green-700 leading-tight">{cat.name}</span>
                        </Link>
                    );
                })}
            </div>
        </section>
    );
}

function FeaturedSection({ products, loading }) {
    return (
        <section className="sg-container py-10">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Nové produkty</h2>
                <Link to="/kategorie" className="text-sm font-semibold text-green-600 hover:text-green-700 flex items-center gap-1" data-testid="see-all-products-btn">
                    Celý katalog <ArrowRight size={14} />
                </Link>
            </div>
            {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Array(4).fill(0).map((_, i) => <div key={i} className="h-48 bg-white rounded-2xl animate-pulse" />)}
                </div>
            ) : products.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {products.slice(0, 8).map(p => <ProductCard key={p.id} product={p} />)}
                </div>
            ) : (
                <p className="text-sm text-gray-500 text-center py-8">Produkty se načítají...</p>
            )}
        </section>
    );
}

function BestsellersSection({ products }) {
    return (
        <section className="bg-gradient-to-b from-green-50/60 to-transparent" data-testid="bestsellers-section">
            <div className="sg-container py-10">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-green-600 flex items-center justify-center">
                            <Flame size={18} className="text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">Nejprodavanejsi</h2>
                    </div>
                    <Link to="/kategorie" className="text-sm font-semibold text-green-600 hover:text-green-700 flex items-center gap-1">
                        Cely katalog <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {products.slice(0, 8).map(p => (
                        <Link key={p.id} to={`/produkt/${p.slug}`}
                            className="flex items-center gap-3 px-3 py-3 rounded-xl border border-gray-100 hover:border-green-200 hover:bg-green-50/50 transition-all group bg-white"
                            data-testid={`bestseller-home-${p.slug}`}>
                            <div className="w-16 h-16 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                {p.main_image ? (
                                    <img src={p.main_image} alt={p.name} className="w-full h-full object-contain p-1" loading="lazy" />
                                ) : (
                                    <Package size={18} className="text-gray-300" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                {p.brand && <p className="text-[10px] text-gray-400 font-medium uppercase tracking-wide mb-0.5">{p.brand}</p>}
                                <p className="text-sm font-medium text-gray-700 group-hover:text-green-700 leading-tight line-clamp-2 transition-colors">{p.name}</p>
                                <p className="font-bold text-sm mt-1" style={{ color: "var(--sg-primary)" }}>
                                    {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kc
                                    {p.sale_price && p.price > p.sale_price && (
                                        <span className="text-gray-400 line-through font-normal ml-1 text-xs">{p.price.toLocaleString("cs-CZ")} Kc</span>
                                    )}
                                </p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

function WhyUsSection() {
    return (
        <section className="bg-white border-y border-gray-100">
            <div className="sg-container py-12">
                <h2 className="text-xl font-bold text-gray-900 mb-8 text-center">Proč Solar-Green?</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {WHY_US.map((w, i) => (
                        <div key={i} className="flex flex-col items-center text-center gap-3 p-4">
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-green-600 shadow-lg shadow-green-200">
                                <w.icon size={20} className="text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-sm text-gray-900 mb-1">{w.title}</p>
                                <p className="text-xs text-gray-500 leading-relaxed">{w.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function BrandsSection({ brandConfigs }) {
    const brands = brandConfigs.length > 0
        ? brandConfigs
        : BRANDS.map(b => ({ name: b, logo_url: null }));

    return (
        <section className="sg-container py-10">
            <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Značky, které prodáváme</h2>
            <div className="flex flex-wrap justify-center gap-3">
                {brands.map(b => (
                    <Link key={b.name} to={`/kategorie?brand=${b.name}`}
                        className="flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-100 rounded-full hover:border-green-300 hover:shadow-md hover:shadow-green-100/50 transition-all group">
                        {b.logo_url ? (
                            <img src={b.logo_url} alt={b.name} className="h-12 w-auto object-contain" loading="lazy" />
                        ) : (
                            <span className="text-sm font-semibold text-gray-600 group-hover:text-green-700">{b.name}</span>
                        )}
                    </Link>
                ))}
            </div>
        </section>
    );
}

function BlogSection({ posts }) {
    return (
        <section className="sg-container py-10">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Z blogu</h2>
                <Link to="/blog" className="text-sm font-semibold text-green-600 hover:text-green-700 flex items-center gap-1">
                    Všechny články <ArrowRight size={14} />
                </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-5">
                {posts.slice(0, 3).map((post, i) => (
                    <Link key={i} to="/blog" className="group block bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                        {post.image && (
                            <img src={post.image} alt={post.title} className="w-full h-40 object-cover group-hover:opacity-90 transition-opacity" />
                        )}
                        <div className="p-4">
                            <p className="text-sm font-bold text-gray-800 group-hover:text-green-700 line-clamp-2 mb-1">{post.title}</p>
                            <p className="text-xs text-gray-500 line-clamp-2">{post.excerpt}</p>
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
}

function CTASection({ theme }) {
    return (
        <section className="sg-container py-6 mb-8">
            <div className="rounded-3xl p-8 sm:p-10 text-white flex items-center justify-between gap-6 flex-wrap"
                style={{ background: "linear-gradient(135deg, #1a472a, #389622)" }}>
                <div>
                    <h3 className="font-bold text-xl mb-2">Potřebujete poradit s výběrem?</h3>
                    <p className="text-sm text-white/80">Zavolejte nám nebo pošlete poptávku - odborná konzultace zdarma.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <a href={`tel:${theme.phone}`} className="flex items-center gap-2 bg-white font-bold text-sm px-6 py-3 rounded-full shadow-lg"
                        style={{ color: "#1a472a" }}>
                        <Phone size={15} /> {theme.phone}
                    </a>
                    <Link to="/sluzby" className="text-white border-2 border-white/40 font-bold text-sm px-6 py-3 rounded-full hover:bg-white/10 hover:border-white/60 transition-all">
                        Poptávka zdarma
                    </Link>
                </div>
            </div>
        </section>
    );
}
